import React from 'react';
import PropTypes from 'prop-types';
import { useObjectiveMetadata } from './useObjectiveMetadata';

const STATUS_LABELS = {
  not_started: 'Non commencé',
  in_progress: 'En cours',
  blocked: 'Bloqué',
  done: 'Terminé',
};

const STATUS_COLORS = {
  not_started: 'bg-slate-200 text-slate-700',
  in_progress: 'bg-blue-100 text-blue-700',
  blocked: 'bg-rose-100 text-rose-700',
  done: 'bg-emerald-100 text-emerald-700',
};

const ObjectiveCompactList = ({ objectives = [] }) => {
  const { getProjectName, getOwnerName, getSprintName } = useObjectiveMetadata(objectives);
  if (!objectives.length) {
    return (
      <div className="rounded-xl border border-indigo-100 bg-white px-4 py-3 text-sm text-slate-500">
        Aucun objectif à afficher.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {objectives.map((objective) => {
        const statusLabel = STATUS_LABELS[objective.status] || objective.status;
        const statusColor = STATUS_COLORS[objective.status] || 'bg-slate-200 text-slate-700';
        const projectLabel = getProjectName(objective);
        const ownerLabel = getOwnerName(objective);
        const sprintLabel = getSprintName(objective);

        return (
          <div
            key={objective.id}
            className="rounded-2xl border border-indigo-100/60 bg-white/90 p-4 shadow-sm"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">{objective.name}</p>
                <p className="text-xs text-slate-500">{projectLabel}</p>
              </div>
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColor}`}>
                {statusLabel}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-600">
              <div>
                <span className="block text-[11px] uppercase tracking-wide text-slate-400">Owner</span>
                {ownerLabel}
              </div>
              <div>
                <span className="block text-[11px] uppercase tracking-wide text-slate-400">Effort</span>
                {objective.effort ?? '—'}
              </div>
              {sprintLabel && sprintLabel !== 'Non précisé' && (
                <div>
                  <span className="block text-[11px] uppercase tracking-wide text-slate-400">Sprint</span>
                  {sprintLabel}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

ObjectiveCompactList.propTypes = {
  objectives: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string.isRequired,
      status: PropTypes.string,
      effort: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      project_name: PropTypes.string,
      project: PropTypes.object,
      owner_name: PropTypes.string,
      owner_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      owner: PropTypes.object,
      sprint_name: PropTypes.string,
      sprint_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      sprint: PropTypes.object,
    })
  ),
};

export default ObjectiveCompactList;
